<template>
	<v-card tile elevation="0" style="height: 100%; overflow-y: hidden">
		<v-card-title class="card-header text-left fixed-card-title2">
			<v-row no-gutters>
				<v-col class="align-center d-flex" cols="10">
					<v-toolbar-title>{{ $t('add-observation-header') }}</v-toolbar-title>
				</v-col>
				<v-spacer />
				<v-icon color="white" @click="closeDrawer">mdi-close</v-icon>
			</v-row>
		</v-card-title>

		<v-card-text class="pt-4 pb-0 overflow-y-auto" style="height: calc(100vh - 221px)">
			<v-row dense class="ma-0">
				<v-col cols="12">
					<v-select
						outlined
						v-model="performedBy"
						:label="$t('staff')"
						:items="medicalStaff"
						return-object
						item-text="fullName"
						:menu-props="{ maxHeight: 200, offsetY: true }"
						hide-details />
				</v-col>
				<v-col cols="12">
					<v-select
						v-model="selectedObservationCategoryOption"
						:items="observationCategoryOptions"
						item-value="value"
						item-text="label"
						:label="$t('category')"
						outlined
						hide-details
						:menu-props="{ maxHeight: 199, offsetY: true }">
						<template v-slot:item="{ item }">
							<v-tooltip left>
								<template v-slot:activator="{ on, attrs }">
									<span v-bind="attrs" v-on="on" style="width: 100%">{{ item.value }} </span>
								</template>
								<span>{{ item.description }}</span>
							</v-tooltip>
						</template>
					</v-select>
				</v-col>
				<v-col cols="12">
					<v-select
						v-model="selectedDiagnose"
						:items="diagnoses"
						item-value="diagnosisCode"
						item-text="diagnosisCode"
						:label="$t('diagnose')"
						outlined
						hide-details
						:menu-props="{ maxHeight: 199, offsetY: true }">
						<template v-slot:item="{ item }">
							<v-tooltip left>
								<template v-slot:activator="{ on, attrs }">
									<span v-bind="attrs" v-on="on" style="width: 100%; max-width: 408px">
										{{ item.diagnoseDescription }}
									</span>
								</template>
								<span>{{ item.diagnosisCode }}</span>
							</v-tooltip>
						</template>
					</v-select>
				</v-col>
				<v-col cols="12" class="mx-0">
					<v-textarea
						outlined
						dense
						v-model="observationText"
						:label="$t('observation-text')"
						hide-details
						no-resize
						row-height="45"
						rows="12" />
				</v-col>
			</v-row>
		</v-card-text>
		<v-container class="px-4">
			<v-row dense class="ma-0">
				<v-col cols="12" class="mx-0">
					<InputTimeFormatter
						:inputValue="timeSpentSeconds"
						@update:inputValue="(val) => (timeSpentSeconds = val)"
						:inputLabel="$t('time-spent')"
						:dense="false" />
				</v-col>
			</v-row>
			<v-row dense class="ma-0">
				<v-col cols="6" order="1">
					<v-btn @click="closeDrawer()" color="secondary" style="color: black" block>{{ $t('cancel') }}</v-btn>
				</v-col>
				<v-tooltip top v-if="!addButtonEnabled">
					<template v-slot:activator="{ on, attrs }">
						<v-col cols="6" v-bind="attrs" v-on="on" order="2">
							<v-btn disabled block>{{ $t('add') }}</v-btn>
						</v-col>
					</template>
					<span>{{ $t('fill-all-fields-warning') }}</span>
				</v-tooltip>
				<v-col cols="6" v-else order="2">
					<v-btn @click="addObservation()" color="dark_purple" style="color: white" block>
						{{ $t('add') }}
					</v-btn>
				</v-col>
			</v-row>
		</v-container>
	</v-card>
</template>

<script>
import { mapActions, mapState } from 'vuex';
import { getRowsForDataTable } from '@/utils/utilities';
import InputTimeFormatter from '../Client/components/InputTimeFormatter.vue';

export default {
	name: 'CCMObservationAdd',
	data () {
		return {
			medicalStaff: null,
			timeSpentSeconds: 0,
			performedBy: null,
			observationCategoryOptions: [],
			selectedObservationCategoryOption: null,
			observationText: '',
			selectedDiagnose: null,
		};
	},
	computed: {
		...mapState({
			userData: (state) => state.authentication.userData,
			detailedUser: (state) => state.users.detailedUser,
			currentRecord: (state) => state.ccmRecords.ccmRecord,
			diagnoses: (state) => state.ccmDiagnoses.ccmDiagnosesByPatientId,
			roleList: (state) => state.roles.roles,
		}),
		addButtonEnabled () {
			return (
				this.selectedObservationCategoryOption &&
				this.performedBy &&
				this.observationText.trim() &&
				this.timeSpentSeconds &&
				this.selectedDiagnose &&
				this.timeSpentSeconds <= 7200
			);
		},
	},

	methods: {
		...mapActions({
			getObservationCategoryOptionsTemplate: 'ccmRecords/getObservationCategoryOptionsTemplate',
			getDiagnosisByPatientId: 'ccmDiagnoses/getCCMDiagnosesByPatientId',
			storeCCMRecordsByPatientId: 'ccmRecords/storeMinimalCCMRecordsByPatientId',
		}),
		getRowsForDataTable,
		closeDrawer () {
			this.timeSpentSeconds = 0;
			this.performedBy = null;
			this.observationText = '';
			this.selectedDiagnose = null;
			this.selectedObservationCategoryOption = null;
			this.$store.commit('SET_DETAILS_DRAWER', false);
		},

		async addObservation () {
			const body = {
				ccmRecordId: this.currentRecord.id,
				patientId: this.currentRecord.patientId,
				hospitalId: this.currentRecord.hospitalId,
				observationDate: new Date().toISOString(),
				category: this.selectedObservationCategoryOption,
				observationText: this.observationText,
				staffId: this.userData.id,
				performedBy: this.performedBy.fullName,
				performedByRole: this.roleList.find((item) => item.id === this.performedBy.roleId).roleName,
				timeSpentSeconds: this.timeSpentSeconds,
				relatedDiagnosisCode: this.selectedDiagnose,
				additionalData: '',
			};
			try {
				await this.$store.dispatch('ccmObservation/addCCMObservation', body);
				await this.$store.dispatch('ccmObservation/getCCMObservationByPatientId', this.currentRecord.patientId);
			} catch (error) {
				// Change this to something more suitable
				this.$store.commit('alerts/add', {
					id: 'addedObservation',
					type: 'error',
					color: 'main_red',
					message: this.$t('failed!'),
				});
			} finally {
				await this.storeCCMRecordsByPatientId(this.currentRecord.patientId);
				this.closeDrawer();
			}
		},

		filterMedicalStaff () {
			return this.detailedUser.userData.medicalTeamsData[0].membersList
				.filter((member) => (member.roleId === 3 || member.roleId === 4) && member.isSequoiaUser && member.status === 1)
				.map((member) => ({
					...member,
					fullName: `${member.firstName} ${member.lastName}`,
				}));
		},
	},
	async mounted () {
		this.medicalStaff = this.filterMedicalStaff();

		const observationCategoryOptionsTemplate = await this.getObservationCategoryOptionsTemplate();
		this.observationCategoryOptions = observationCategoryOptionsTemplate.data.map((item) => ({
			...item,
			label: `${item.value} - ${item.description}`,
		}));

		await this.getDiagnosisByPatientId(this.detailedUser.userData.id);
	},
	components: { InputTimeFormatter },
};
</script>
